import { onMounted, watch } from 'vue'
import { useDocumentVisibility } from '@vueuse/core'

import { ActivityStatus } from '@brighterly/lib-core-types/booking/enums'

import { useUpdateLessonApi } from '../../api/update-lesson.hook'

type PresenceSend = () => Promise<unknown>

interface LessonRoomPresenceOptions {
  bookingId: number
}

interface PresenceControllerReturn {
  trigger: () => Promise<void>
}

const MAX_ATTEMPTS = 3
const RETRY_DELAY = 1000

// Send-once / in-flight / retry policy, decoupled from Vue for testability.
// `send` resolves to null on failure (the api hook catches) → retry.
export const createPresenceController = (send: PresenceSend): PresenceControllerReturn => {
  let inFlight = false

  const trigger = async (): Promise<void> => {
    if (inFlight) return
    inFlight = true

    try {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        const result = await send()
        if (result !== null) return
        if (attempt < MAX_ATTEMPTS) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY))
        }
      }
    } finally {
      inFlight = false
    }
  }

  return { trigger }
}

// Tells BE "the kid is in the room": PATCH activity_status=WEB once on mount
// (BE derives IN_PROGRESS + starts accruing actual_duration per ping) and again
// when the tab returns to visible (the OFFLINE cron flips the status after ~30s
// without pings; nothing else would ever re-set WEB).
export const useLessonRoomPresence = ({ bookingId }: LessonRoomPresenceOptions): void => {
  const { updateLesson } = useUpdateLessonApi()
  const visibility = useDocumentVisibility()

  const { isDesktop } = useDevice()
  const deviceType: 'DESKTOP' | 'MOBILE' = isDesktop ? 'DESKTOP' : 'MOBILE'

  const { trigger } = createPresenceController(() => updateLesson(bookingId, {
    activity_status: ActivityStatus.Web,
    device_type: deviceType,
  }))

  onMounted(() => {
    if (!bookingId) return
    void trigger()
  })

  watch(visibility, (current, previous) => {
    if (!bookingId) return
    if (current === 'visible' && previous === 'hidden') void trigger()
  })
}
